/*
 * @Author: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @Date: 2025-07-08 16:35:00
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-07-11 10:18:10
 */
import React from 'react'
import { Tooltip } from 'antd'
import VideoThumbnail from './VideoThumbnail'
import PhotoPreview from './PhotoPreview'
import { judgeUrlMediaType, getVideoThumbnail } from '../utils/mediaUtils'
import useRectifyAnalytics from '../../../hooks/useRectifyAnalytics'

// 记录类型
interface RectifyRecord {
  recordID?: number | string
  id?: number | string
  customID?: number | string
  recordNo?: string
  [key: string]: any
}

// Props 类型
interface PhotoWithTooltipProps {
  photos: string[] | null | undefined
  type: string
  record: RectifyRecord
  activeTooltip: string | null
  setActiveTooltip: (tooltip: string | null) => void
  handlePhotoPreview: (photos: string[], index: number, record?: RectifyRecord) => void
}

/**
 * 渲染带有Tooltip的照片缩略图
 * @param {object} props - 组件属性
 * @param {Array} props.photos - 照片数组
 * @param {string} props.type - 照片类型，如"整改前"或"整改后"
 * @param {object} props.record - 记录对象，用于生成唯一ID
 * @param {string} props.activeTooltip - 当前激活的Tooltip ID
 * @param {function} props.setActiveTooltip - 设置激活Tooltip的函数
 * @param {function} props.handlePhotoPreview - 处理照片预览的函数
 * @returns {JSX.Element} - 照片缩略图组件
 */
const PhotoWithTooltip: React.FC<PhotoWithTooltipProps> = ({
  photos,
  type,
  record,
  activeTooltip,
  setActiveTooltip,
  handlePhotoPreview
}) => {
  // 使用埋点hook
  const { trackRectifyEvent } = useRectifyAnalytics()

  if (!photos || photos.length === 0) return <span>未上传</span>

  const mainPhoto = photos[0] // 显示第一张照片作为缩略图
  // 兼容不同业务的唯一标识：优先使用recordID，其次id/customID/recordNo
  const recordKey =
    record?.recordID || record?.id || record?.customID || record?.recordNo
  const tooltipId = `${type}-${recordKey}`
  const isMainPhotoVideo = judgeUrlMediaType(mainPhoto) === 'video'

  // 获取主图的缩略图URL（如果是视频则获取截图）
  const mainThumbnail = isMainPhotoVideo
    ? getVideoThumbnail(mainPhoto)
    : mainPhoto

  // 添加埋点的照片预览处理函数
  const handlePhotoPreviewWithTracking = (photos: string[], index: number) => {
    // 上报点击图片埋点
    trackRectifyEvent(
      'clickPicture',
      {},
      {
        recordId: recordKey,
        photoCount: photos.length
      }
    )

    // 调用原有的照片预览函数
    handlePhotoPreview(photos, index, record)
  }

  // 如果只有一张照片，直接显示缩略图，点击后预览
  if (photos.length === 1) {
    return (
      <div className="photo-thumbnail-wrapper">
        <div
          className="photo-thumbnail-container"
          onClick={() => handlePhotoPreviewWithTracking(photos, 0)}
        >
          {isMainPhotoVideo ? (
            <VideoThumbnail src={mainThumbnail} alt={`${type}视频`} />
          ) : (
            <img src={mainPhoto} alt={`${type}照片`} className="thumbnail" />
          )}
        </div>
      </div>
    )
  }

  // 计算每行最多显示的图片数量
  const maxImagesPerRow = 5
  // 图片尺寸和间距
  const imageSize = 87
  const imageGap = 4

  // 计算tooltip内容宽度：如果图片数量小于等于5，根据实际数量计算；否则固定为5个图片的宽度
  const contentWidth =
    Math.min(photos.length, maxImagesPerRow) * imageSize +
    (Math.min(photos.length, maxImagesPerRow) - 1) * imageGap

  // 创建tooltip内容，显示所有照片的预览
  const tooltipContent = (
    <div
      className="photo-preview-tooltip"
      style={{
        width: `${contentWidth}px`
      }}
    >
      {photos.map((photo, index) => (
        <PhotoPreview
          key={index}
          photo={photo}
          type={type}
          index={index}
          onClick={() => handlePhotoPreviewWithTracking(photos, index)}
        />
      ))}
    </div>
  )

  return (
    <div className="photo-thumbnail-wrapper">
      <Tooltip
        title={tooltipContent}
        classNames={{ root: 'photo-tooltip' }}
        placement="top"
        mouseEnterDelay={0.5}
        destroyTooltipOnHide
        open={activeTooltip === tooltipId}
        onOpenChange={(open) => {
          if (open) {
            setActiveTooltip(tooltipId)
          } else if (activeTooltip === tooltipId) {
            setActiveTooltip(null)
          }
        }}
        // 动态设置Tooltip样式，加上padding
        styles={{
          root: {
            maxWidth: 'none',
            width: `${contentWidth + 16}px` // 加上padding
          }
        }}
      >
        <div
          className="photo-thumbnail-container"
          onClick={() => handlePhotoPreviewWithTracking(photos, 0)}
        >
          {isMainPhotoVideo ? (
            <VideoThumbnail src={mainThumbnail} alt={`${type}视频`} />
          ) : (
            <img src={mainPhoto} alt={`${type}照片`} className="thumbnail" />
          )}
          {photos.length > 1 && (
            <span className="photo-count">{photos.length}</span>
          )}
        </div>
      </Tooltip>
    </div>
  )
}

export default PhotoWithTooltip
